import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { userService } from "./user.service";
import type { GetUserCountParams, GetUsersParams } from "./user.types";

// ==================== QUERY KEYS ====================

export const userKeys = {
	// Base keys
	all: ["user"] as const,

	// Users
	users: () => [...userKeys.all, "users"] as const,
	usersList: (start: number, count: number, params?: GetUsersParams) =>
		[...userKeys.users(), "list", start, count, params] as const,
	usersDetail: (userId: string) =>
		[...userKeys.users(), "detail", userId] as const,
	usersCount: (params?: GetUserCountParams) =>
		[...userKeys.users(), "count", params] as const,
	usersPermissions: (userId: string) =>
		[...userKeys.users(), userId, "permissions"] as const,
	usersAll: (params?: GetUsersParams) =>
		[...userKeys.users(), "all", params] as const,
	usersStatuses: (forPlatformLevelUsers = false) =>
		[...userKeys.users(), "statuses", forPlatformLevelUsers] as const,
	platformRoles: () => [...userKeys.all, "platformRoles"] as const,
	emailExists: (email: string) =>
		[...userKeys.users(), "email-exists", email] as const,
	userByEmail: (email: string) =>
		[...userKeys.users(), "by-email", email] as const,
	// Avatar
	avatar: (userId: string) => [...userKeys.users(), "avatar", userId] as const,
	/** GET validate-password — key includes password so checklist + cache stay in sync */
	validatePassword: (userId: string, password: string) =>
		[...userKeys.users(), userId, "validate-password", password] as const,
	// History
	usersHistory: (userId: string) =>
		[...userKeys.users(), "history", userId] as const,
	usersHistoryCount: (userId: string) =>
		[...userKeys.usersHistory(userId), "count"] as const,
	usersHistoryList: (userId: string, start: number, count: number) =>
		[...userKeys.usersHistory(userId), "list", start, count] as const,
} as const;

// ==================== USER QUERY OPTIONS ====================

export const userQueryOptions = {
	/**
	 * Get a user by their ID
	 */
	detail: (userId: string) =>
		queryOptions({
			queryKey: userKeys.usersDetail(userId),
			queryFn: () => userService.getUserById(userId),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			enabled: !!userId && userId.length > 0,
			select: (data) => {
				// alphabetize the data in organizationUserInfo array
				data.organizationUserInfo.sort(
					(a, b) =>
						a.organization?.name?.localeCompare(b.organization?.name || "") ||
						0,
				);
				return data;
			},
		}),

	/**
	 * Get users with pagination and optional filtering
	 */
	list: (start: number = 1, count: number = 25, params?: GetUsersParams) =>
		queryOptions({
			queryKey: userKeys.usersList(start, count, params),
			queryFn: () => userService.getUsers(start, count, params),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get total count of users, optionally filtered
	 */
	count: (params?: GetUserCountParams) =>
		queryOptions({
			queryKey: userKeys.usersCount(params),
			queryFn: () => userService.getUserCount(params),
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get a user's permissions
	 */
	permissions: (userId: string) =>
		queryOptions({
			queryKey: userKeys.usersPermissions(userId),
			queryFn: () => userService.getUserPermissions(userId),
			staleTime: 10 * 60 * 1000, // 10 minutes (permissions change less frequently)
			gcTime: 30 * 60 * 1000, // 30 minutes
			enabled: !!userId && userId.length > 0,
		}),

	/**
	 * Platform roles
	 */
	platformRoles: () =>
		queryOptions({
			queryKey: userKeys.platformRoles(),
			queryFn: () => userService.getPlatformRoles(),
			staleTime: 10 * 60 * 1000, // 10 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
		}),

	/**
	 * User statuses
	 */
	statuses: (forPlatformLevelUsers = false) =>
		queryOptions({
			queryKey: userKeys.usersStatuses(forPlatformLevelUsers),
			queryFn: () => userService.getUserStatuses(forPlatformLevelUsers),
			staleTime: 30 * 60 * 1000, // 30 minutes (statuses rarely change)
			gcTime: 60 * 60 * 1000, // 60 minutes
		}),

	/**
	 * Check if an email is already in use (platform-wide)
	 * Returns true if the email exists, false otherwise
	 * Useful for validating email uniqueness during user creation/update
	 */
	emailExists: (email: string) =>
		queryOptions({
			queryKey: userKeys.emailExists(email),
			queryFn: async () => {
				const count = await userService.getUserCount({ Email: email });
				return count > 0;
			},
			staleTime: 30 * 1000, // 30 seconds (email availability can change)
			gcTime: 60 * 1000, // 1 minute
			enabled: !!email && email.length > 0 && email.includes("@"),
		}),

	/**
	 * Get user details by email (platform-wide)
	 * Returns the user object if found, null otherwise
	 * Useful for fetching existing user info during org user creation
	 */
	getByEmail: (email: string) =>
		queryOptions({
			queryKey: userKeys.userByEmail(email),
			queryFn: async () => {
				const users = await userService.getUsers(1, 1, { Email: email });
				return users.length > 0 ? users[0] : null;
			},
			staleTime: 30 * 1000, // 30 seconds
			gcTime: 60 * 1000, // 1 minute
			enabled: !!email && email.length > 0 && email.includes("@"),
		}),

	/**
	 * Get a user's avatar as a Blob
	 * Returns the avatar image blob or null if not found
	 */
	avatar: (userId: string) =>
		queryOptions({
			queryKey: userKeys.avatar(userId),
			queryFn: async () => {
				try {
					return await userService.getUserAvatar(userId);
				} catch {
					// Avatar not found or error - return null
					return null;
				}
			},
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 30 * 60 * 1000, // 30 minutes
			enabled: !!userId && userId.length > 0,
			retry: false, // Don't retry if avatar doesn't exist
		}),

	/**
	 * Infinite query for users (for infinite scroll/virtual lists)
	 */
	infinite: (
		params?: GetUsersParams,
		pageSize: number = 10,
		totalCount?: number,
	) =>
		infiniteQueryOptions({
			queryKey: [...userKeys.usersList(1, pageSize, params), "infinite"],
			queryFn: ({ pageParam = 1 }) =>
				userService.getUsers(pageParam, pageSize, params),
			initialPageParam: 1,
			getNextPageParam: (lastPage, _allPages, lastPageParam) => {
				// If we have a total count, check against it for accuracy
				if (totalCount !== undefined) {
					const fetched = _allPages.reduce((acc, page) => acc + page.length, 0);
					if (fetched >= totalCount) {
						return undefined;
					}
				}
				// If the last page has fewer items than pageSize, we've reached the end
				if (lastPage.length < pageSize) {
					return undefined;
				}
				// Calculate next page start index
				return lastPageParam + pageSize;
			},
			getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
				if (firstPageParam <= 1) {
					return undefined;
				}
				return Math.max(1, firstPageParam - pageSize);
			},
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
		}),

	/**
	 * Get all platform roles
	 */
	// platformRoles: () =>
	// 	queryOptions({
	// 		queryKey: userKeys.platformRoles(),
	// 		queryFn: () => userService.getPlatformRoles(),
	// 		staleTime: 30 * 60 * 1000, // 30 minutes (roles change infrequently)
	// 		gcTime: 60 * 60 * 1000, // 60 minutes
	// 	}),

	history: {
		count: (userId: string) =>
			queryOptions({
				queryKey: userKeys.usersHistoryCount(userId),
				queryFn: () => userService.getUserHistoryCount(userId),
				enabled: !!userId,
			}),

		infinite: (
			userId: string,
			pageSize: number,
			totalCount: number | undefined,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...userKeys.usersHistoryList(userId, 1, pageSize),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					userService.getUserHistory(userId, pageParam, pageSize),
				// Newest-first: start at the last page and work backwards.
				// Only runs once totalCount is known and non-zero.
				initialPageParam: Math.max(1, (totalCount ?? 0) - pageSize + 1),
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1)
						return undefined;
					return firstPageParam + pageSize;
				},
				enabled: !!userId && totalCount !== undefined && totalCount > 0,
			}),
	},
};

// ==================== CONVENIENCE EXPORTS ====================

/**
 * All user query options grouped by category
 */
export const userQueries = {
	users: userQueryOptions,
} as const;

/**
 * Query key factory for easy invalidation
 */
export const userQueryKeys = userKeys;

// ==================== UTILITY FUNCTIONS ====================

/**
 * Helper to invalidate all user-related queries
 */
export function getUserInvalidationKeys() {
	return {
		all: userKeys.all,
		users: userKeys.users(),
		userDetail: (userId: string) => userKeys.usersDetail(userId),
		userPermissions: (userId: string) => userKeys.usersPermissions(userId),
		platformRoles: userKeys.platformRoles(),
		emailExists: (email: string) => userKeys.emailExists(email),
		userByEmail: (email: string) => userKeys.userByEmail(email),
	};
}

/**
 * Helper to get query keys for a specific user and all its nested resources
 */
export function getUserRelatedKeys(userId: string) {
	return {
		user: userKeys.usersDetail(userId),
		permissions: userKeys.usersPermissions(userId),
	};
}

/**
 * Helper to create query options with consistent defaults
 */
export function createUserQueryOptions<T>(
	queryKey: readonly unknown[],
	queryFn: () => Promise<T>,
	options: {
		staleTime?: number;
		gcTime?: number;
		enabled?: boolean;
	} = {},
) {
	return queryOptions({
		queryKey,
		queryFn,
		staleTime: options.staleTime ?? 5 * 60 * 1000, // Default 5 minutes
		gcTime: options.gcTime ?? 10 * 60 * 1000, // Default 10 minutes
		enabled: options.enabled ?? true,
	});
}
